import { useMemo } from 'react';
import { Badge } from '../ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Loader2, History, TrendingUp, TrendingDown } from 'lucide-react';
import { format } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

export default function TargetHistoryModal({ open, onClose, historyItem, historyData, loading }) {
  const chartData = useMemo(() => {
    if (!historyData.length) return [];
    return [...historyData]
      .filter(h => h.new_price !== null)
      .reverse()
      .map((h, index) => ({
        date: format(new Date(h.changed_at), 'MMM d HH:mm'),
        fullDate: format(new Date(h.changed_at), 'MMM d, yyyy HH:mm:ss'),
        price: h.new_price,
        index: index + 1,
      }));
  }, [historyData]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl max-h-[80vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <History className="h-5 w-5" />
            Target History - {historyItem?.sku_code}
          </DialogTitle>
        </DialogHeader>
        <div className="flex-1 overflow-auto space-y-4">
          {loading ? (
            <div className="py-8 text-center"><Loader2 className="h-6 w-6 animate-spin mx-auto" /></div>
          ) : historyData.length === 0 ? (
            <div className="py-8 text-center text-muted-foreground">No history available</div>
          ) : (
            <>
              {chartData.length > 1 && (
                <Card className="rounded-sm">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <TrendingUp className="h-4 w-4" />
                      Price Trend (Last 90 Days)
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="h-[200px]">
                      <ResponsiveContainer width="100%" height="100%">
                        <LineChart data={chartData}>
                          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                          <XAxis dataKey="index" tick={{ fontSize: 11 }} stroke="#6b7280" label={{ value: 'Change #', position: 'insideBottom', offset: -5, fontSize: 10 }} />
                          <YAxis tick={{ fontSize: 11 }} stroke="#6b7280" tickFormatter={(v) => `$${v}`} />
                          <Tooltip
                            content={({ active, payload }) => {
                              if (active && payload && payload.length) {
                                const data = payload[0].payload;
                                return (
                                  <div className="bg-white border rounded-sm shadow-lg p-2 text-xs">
                                    <p className="font-semibold text-gray-700">{data.fullDate}</p>
                                    <p className="text-emerald-600 font-mono font-bold">${data.price.toFixed(2)}</p>
                                  </div>
                                );
                              }
                              return null;
                            }}
                          />
                          <Line type="monotone" dataKey="price" stroke="#10b981" strokeWidth={2} dot={{ r: 4 }} activeDot={{ r: 6 }} />
                        </LineChart>
                      </ResponsiveContainer>
                    </div>
                  </CardContent>
                </Card>
              )}

              <Card className="rounded-sm">
                <CardContent className="p-0">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-muted/50">
                        <TableHead className="text-xs">Date & Time</TableHead>
                        <TableHead className="text-xs">Change</TableHead>
                        <TableHead className="text-xs">Price</TableHead>
                        <TableHead className="text-xs">Changed By</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {historyData.map((h) => (
                        <TableRow key={h.id}>
                          <TableCell className="text-xs">
                            <div>{format(new Date(h.changed_at), 'MMM d, yyyy')}</div>
                            <div className="text-muted-foreground">{format(new Date(h.changed_at), 'HH:mm:ss')}</div>
                          </TableCell>
                          <TableCell>
                            <Badge variant={h.change_type === 'CREATED' ? 'default' : h.change_type === 'DELETED' ? 'destructive' : 'secondary'} className="text-xs">
                              {h.change_type}
                            </Badge>
                          </TableCell>
                          <TableCell className="font-mono">
                            {h.old_price !== null && (
                              <span className="text-muted-foreground line-through mr-2">${h.old_price?.toFixed(2)}</span>
                            )}
                            {h.new_price !== null ? (
                              <span className="font-semibold text-emerald-600">${h.new_price?.toFixed(2)}</span>
                            ) : (
                              <span className="text-destructive">Deleted</span>
                            )}
                            {h.old_price !== null && h.new_price !== null && (
                              <span className={`ml-2 text-xs ${h.new_price > h.old_price ? 'text-red-500' : 'text-emerald-500'}`}>
                                {h.new_price > h.old_price ? <TrendingUp className="inline h-3 w-3" /> : <TrendingDown className="inline h-3 w-3" />}
                                {Math.abs(((h.new_price - h.old_price) / h.old_price) * 100).toFixed(1)}%
                              </span>
                            )}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">{h.changed_by_name || 'System'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </CardContent>
              </Card>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
